import {AfterViewInit, ChangeDetectionStrategy, Component, Input, OnInit, TemplateRef, ViewChild} from '@angular/core';
import {MatTable, MatTableDataSource} from '@angular/material/table';
import {MatPaginator} from '@angular/material/paginator';
import {MatSort} from '@angular/material/sort';
import {BehaviorSubject, combineLatest, merge, Observable} from 'rxjs';
import {FormBuilder, FormControl, FormGroup} from '@angular/forms';
import {map, startWith, takeUntil} from 'rxjs/operators';
import {SelectionModel} from '@angular/cdk/collections';
import {BreakpointObserver} from '@angular/cdk/layout';
import {BaseComponent} from 'nc-shared';
import {SelectConfig} from '../model/select-config';
import {TableData} from '../model/table-data';
import {Column} from '../model/filter/column';
import {Order} from '../model/filter/order';
import {DatatableColumn} from '../model/column/datatable-column';
import {ColumnTypeEnumeration} from '../model/column/column-type.enumeration';
import {ActionColumn} from '../model/column/action-column';
import {ColumnBuilder} from '../utils/column.builder';
import {dateToString, parseDate, StringsUtils} from 'nc-utils';
import {Search} from '../model/filter/search';
import {SelectColumn} from '../model/column/select-column';
import {TranslateService} from '@ngx-translate/core';
import {FilterConfig} from '../model/filter/filter-config';
import {AlignColumnsEnumeration} from '../model/column/align-columns.enumeration';

@Component({
  selector: 'nc-datatable',
  templateUrl: './nc-datatable.component.html',
  styleUrls: ['./nc-datatable.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NcDatatableComponent extends BaseComponent implements OnInit, AfterViewInit {
  private _columns: Array<DatatableColumn>;

  @Input() set columns(columns: Array<DatatableColumn>) {
    this._columns = columns;
    this.initColumns();
  }

  get columns(): Array<DatatableColumn> {
    return this._columns;
  }

  @Input() data$: Observable<TableData>;
  @Input() isTotalVisible: boolean = false;
  @Input() mobileBreakpointWidth: number = 900;
  @Input() rowClick: (rowData) => void;
  @Input() selectConfig: SelectConfig;
  @Input() hasPaginator = true;
  @Input() tableHeader: TemplateRef<any>;
  @Input() serverSideFilter: (start: number, length: number, order: Order[], columns: Column[], globalSearch: Search) => void;
  @Input() filterConfig: FilterConfig;
  @Input() id: string;

  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) matSort: MatSort;
  @ViewChild(MatTable) table: MatTable<any>;

  columnsToDisplay: string[];
  columnsFilters: string[];
  dataSource: MatTableDataSource<any>;
  totalRowData: { [key: string]: string };
  dataLength: number = 0;
  selection = new SelectionModel<any>(true, []);
  mobile$: Observable<boolean>;
  loadingSubject$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  loading$ = this.loadingSubject$.asObservable();
  GLOBAL_FILTER_NAME = 'globalFilter';
  sortTableColumn: string;
  sortTableByAscOrDesc;
  readonly alignmentType = AlignColumnsEnumeration;

  constructor(
    private formBuilder: FormBuilder,
    private breakpointObserver: BreakpointObserver,
    private translateService: TranslateService) {
    super();
    this.dataSource = new MatTableDataSource([]);
    this.totalRowData = {};
  }

  ngOnInit(): void {
    const columnFilterValuesObject = {};
    const globalFilterValue = this.filterConfig.filterValues ? this.filterConfig.filterValues[this.GLOBAL_FILTER_NAME] : '';
    this.filterConfig.columnFiltersFormGroup = new FormGroup({});
    this.filterConfig.globalFilterFormGroup = this.formBuilder.group({globalFilter: globalFilterValue});
    this.initFilters();
    this.initColumns();

    this.filterConfig.columnFiltersFormGroup.patchValue(columnFilterValuesObject);
    this.mobile$ = this.breakpointObserver.observe([`(max-width: ${this.mobileBreakpointWidth}px)`]).pipe(map(state => state.matches));

    this.sortTableColumn = this.filterConfig.sortTableByColumn ? this.filterConfig.sortTableByColumn :
      this._columns.find(x => x.visible).name;

    this.sortTableByAscOrDesc = this.filterConfig.sortByAscOrDesc ? this.filterConfig.sortByAscOrDesc : '';
  }

  ngAfterViewInit(): void {
    if (this.serverSideFilter) {
      this.initServerSideFiltering();
    } else {
      this.dataSource.paginator = this.paginator;
      this.dataSource.sort = this.matSort;
      this.dataSource.sortingDataAccessor = (item, property) => {
        const column = this._columns.find(col => col.name === property);

        let returnData: any;

        if (column.type === ColumnTypeEnumeration.DATE_COLUMN) {
          returnData = parseDate(item[property]);
        } else if (column.type === ColumnTypeEnumeration.NUMBER_COLUMN) {
          returnData = parseInt(item[property], 10);
        } else {
          returnData = item[property];
        }

        return returnData;
      };
      this.initData();
    }

    this.matSort.sortChange.subscribe(value => {
      this.filterConfig.sortTableByColumn = value.active;
      this.filterConfig.sortByAscOrDesc = value.direction;
    });
  }

  onRowClick(rowData): void {
    if (this.rowClick) {
      this.rowClick(rowData);
    } else {
      this.selection.toggle(rowData);
    }
  }

  hasPointer(): boolean {
    return this.rowClick != null;
  }

  private initColumns(): void {
    if (this.selectConfig && this._columns.find(x => x.type === ColumnTypeEnumeration.CHECKBOX_COLUMN) == null) {
      const checkboxColumn = ColumnBuilder.createCheckbox();

      this.selectConfig.isFirstColumn
        ? this._columns.unshift(checkboxColumn)
        : this._columns.push(checkboxColumn);
    }

    const visibleColumns = this._columns.filter(x => x.visible === true);
    this.columnsToDisplay = visibleColumns.map(x => x.name);
    this.columnsFilters = visibleColumns.map(x => x.filterName);
  }

  private createFormControl(filterName) {
    const value = this.filterConfig.filterValues ? this.filterConfig.filterValues[filterName] : '';
    return new FormControl(value);
  }

  private initFilters(): void {
    this._columns
      .filter(x => x.searchable)
      .forEach(column => this.filterConfig.columnFiltersFormGroup.addControl(column.filterName, this.createFormControl(column.filterName)));
  }

  hasIcon(column: DatatableColumn): boolean {
    return (column as ActionColumn).iconConfig != null;
  }

  private initServerSideFiltering(): void {
    this.filter(0);

    this.matSort.sortChange.subscribe(() => this.paginator.pageIndex = 0);
    this.filterConfig.columnFiltersFormGroup.valueChanges.subscribe(() => this.paginator.pageIndex = 0);

    merge(
      this.paginator.page,
      this.matSort.sortChange,
      this.filterConfig.columnFiltersFormGroup.valueChanges,
      this.filterConfig.globalFilterFormGroup.valueChanges
    ).subscribe(() => {
      const start = this.paginator.pageIndex * this.paginator.pageSize;
      this.loadingSubject$.next(true);
      this.filter(start);
    });

    this.data$.pipe(takeUntil(this.destroy$))
      .subscribe(tableData => {
        this.dataLength = tableData.totalCount;
        this.dataSource.data = tableData.records;
        this.totalRowData = tableData.total;
        this.paginator.length = tableData.totalCount;
        this.loadingSubject$.next(false);
      });
  }

  private filter(start: number): void {
    const filters = this.filterConfig.columnFiltersFormGroup.getRawValue();
    const globalFilter = this.filterConfig.globalFilterFormGroup.getRawValue();
    const sortedColumnIndex = this._columns.findIndex(x => x.name === this.matSort.active);

    const globalSearch = {value: globalFilter.value} as Search;

    const order = {
      column: sortedColumnIndex,
      direction: this.matSort.direction
    } as Order;

    const columnsWithFilter = this._columns.map(x => {
      return {
        data: x.name,
        name: x.name,
        searchable: x.searchable,
        sortable: x.sortable,
        search: {value: this.getFilterValue(filters[x.filterName], x.type)}
      } as Column;
    });

    this.serverSideFilter(start, this.paginator.pageSize, [order], columnsWithFilter, globalSearch);
  }

  private getFilterValue(value, type: ColumnTypeEnumeration) {
    return type === ColumnTypeEnumeration.DATE_COLUMN ? dateToString(value) : value;
  }

  private initData(): void {
    const globalFilterValue = this.filterConfig.globalFilterFormGroup.value[this.GLOBAL_FILTER_NAME];
    combineLatest([
      this.data$,
      this.filterConfig.columnFiltersFormGroup.valueChanges.pipe(startWith(this.filterConfig.columnFiltersFormGroup.value)),
      this.filterConfig.globalFilterFormGroup.valueChanges.pipe(startWith({globalFilter: globalFilterValue}))
    ]).pipe(
      map(([tableData, columnFilters, globalFilter]) => {
        this.totalRowData = tableData.total;
        return tableData.records.filter(
          record => this.applyGlobalFilter(record, globalFilter) && this.applyColumnFilters(record, columnFilters)
        );
      }),
      takeUntil(this.destroy$)
    ).subscribe(data => {
      this.dataLength = data.length;
      this.dataSource.data = data;
    });
  }

  private applyGlobalFilter(record: { [key: string]: string }, globalFilter): boolean {
    const filterValue = globalFilter.globalFilter ? globalFilter.globalFilter.toLowerCase() : '';
    const visibleColumns = this._columns.filter(x => x.visible).map(x => x.name);
    return visibleColumns.findIndex(column => this.compare(record[column], filterValue)) !== -1;
  }

  private applyColumnFilters(record: { [key: string]: string }, filters): boolean {
    let result = true;
    Object.keys(filters).forEach(filterKey => {
      const filterValue = filters[filterKey];

      if (StringsUtils.isNotEmpty(filterValue?.toString())) {
        const column = this._columns.find(x => x.filterName === filterKey);

        if (column.type === ColumnTypeEnumeration.TEXT_COLUMN) {
          result = result && this.compare(record[column.name], filterValue.toLowerCase());
        } else if (filterValue.length > 0 && column.type === ColumnTypeEnumeration.SELECT_COLUMN) {
          const selectColumn = column as SelectColumn;
          result = result && this.translateService.instant(selectColumn.enumeration[filterValue]).includes(record[column.name]?.toString());
        } else if (column.type === ColumnTypeEnumeration.DATE_COLUMN) {
          result = result && dateToString(filterValue).includes(record[column.name]?.toString());
        }
      }
    });

    return result;
  }

  private compare(columnValue, filterValue: string): boolean {
    return columnValue?.toString()?.toLowerCase()?.includes(filterValue);
  }
}
